import {
  ClonePage as ClonePageComponent,
  ImportChangesModal,
  useSearchParamAction,
  useTimeLeftToEditClone,
} from '@carboncopyco/components';
import {
  selectOrganizationId,
  useGetRecordingQuery,
  useSpliceRecordingMutation,
} from '@carboncopyco/data';
import { type IRecording, RecordingTypes } from '@carboncopyco/types';
import { Button, notify } from '@carboncopyco/ui';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import ErrorPage from '../../components/ErrorPage';
import WorkspaceLayout from '../../components/Workspaces/WorkspaceLayout';
import { useCustomNavigate } from '../../hooks/useCustomNavigate';
import { useFlagsFallback } from '../../hooks/useFlagsFallback';
import { usePageEvent } from '../../hooks/usePageEvent';

type TabId = 'overview' | 'history';
type Params = {
  organizationId: string;
  recordingId: string;
  workspaceId?: string;
  tabId?: TabId;
};

export const CloneScreen = () => {
  const organizationId = useSelector(selectOrganizationId);
  const flags = useFlagsFallback();
  const { recordingId: cloneId, workspaceId } = useParams<Params>();
  const {
    navigateToClonePage,
    navigateToDemoPage,
    navigateToRecordingPreview,
    navigateToUpdateRecording,
    navigateToEditor,
    navigateToClonePageByWorkspace,
    navigateToWorkspace,
    navigateToStudio,
    navigateToFolder,
    links,
  } = useCustomNavigate();
  const navigate = useNavigate();
  const { data: clone, error: cloneError } = useGetRecordingQuery({
    organizationId,
    id: Number(cloneId),
  });

  const [spliceRecording, { isLoading: showSpliceLoader }] =
    useSpliceRecordingMutation();

  const [showImportChangesModal, setShowImportChangesModal] = useState(false);
  const [selectedDemo, setSelectedDemo] = useState<string>('');
  const timeLeftToEditClone = useTimeLeftToEditClone(
    clone || ({} as IRecording),
    flags.platformUpdateCloneTimeLimit
  );

  usePageEvent();

  if (cloneError) {
    notify.error("Error loading clone's data");
  }

  const handleUpdateRecording = async () => {
    if (!clone) {
      return;
    }
    await navigateToUpdateRecording(clone.id);
  };
  useSearchParamAction({
    param: 'continueCloning',
    action: handleUpdateRecording,
    expectedValue: 'true',
    shouldSkip: !clone?.id,
  });

  const handleOnConfirm = () => {
    if (!workspaceId) {
      return;
    }
    const { folderId } = clone ?? {};
    spliceRecording({
      organizationId,
      id: String(cloneId),
      demoId: selectedDemo,
      ...(folderId ? { folderId } : {}),
    })
      .unwrap()
      .then((response) => {
        response.recordingType === RecordingTypes.mobile &&
        !flags.editMobileCloneDemo
          ? navigateToDemoPage(response.id, workspaceId)
          : navigateToEditor(response.id);
        setShowImportChangesModal(false);
      })
      .catch(() => {
        notify.error('Error splicing demo from clone');
        setShowImportChangesModal(false);
      });
  };

  if (cloneError) {
    return (
      <ErrorPage
        title="Error fetching clone"
        buttons={
          <Button
            label="Go to previous page"
            onClick={() => navigate(-1)}
          />
        }
      />
    );
  }

  if (!organizationId || !workspaceId || !cloneId) {
    return null;
  }

  return (
    <WorkspaceLayout>
      <ClonePageComponent
        rootPath={links.getClonePageRoot(Number(cloneId))}
        workspaceId={workspaceId}
        organizationId={organizationId}
        cloneId={cloneId}
        goToClone={({ id }) => navigateToClonePage(id)}
        goToWorkspacePage={navigateToWorkspace}
        goToFolderPage={navigateToFolder}
        goBack={() => navigateToWorkspace(workspaceId)}
        continueCloningProps={{
          onSelect: handleUpdateRecording,
          timeLeftToEditClone: timeLeftToEditClone || '',
          ldFlag: flags?.platformUpdateCloneTimeLimit,
        }}
        demoPagePath={(id) => links.getDemoPageLink(id)}
        goToDemoPage={(demoId) =>
          navigateToDemoPage(String(demoId), workspaceId)
        }
        handleClonePreview={({ id, firstUrl, playerToken }) =>
          navigateToRecordingPreview({
            id,
            firstUrl,
            playerToken,
          })
        }
        openImportEditsModal={() => setShowImportChangesModal(true)}
        spliceCloneLdFlag={flags.platformSpliceFromClone}
        goToCopiedClone={(cId, wsId) =>
          navigateToClonePageByWorkspace(cId, wsId)
        }
        goToStudio={(studioLink) => navigateToStudio(studioLink)}
        goToEditor={navigateToEditor}
      />
      {flags.platformSpliceFromClone && showImportChangesModal && (
        <ImportChangesModal
          open={showImportChangesModal}
          onClose={() => setShowImportChangesModal(false)}
          selectedDemo={selectedDemo}
          setSelectedDemo={setSelectedDemo}
          showLoader={showSpliceLoader}
          loaderLabel="Please wait while creating new demo"
          onConfirm={handleOnConfirm}
          organizationId={organizationId}
        />
      )}
    </WorkspaceLayout>
  );
};
